import React, { useState } from 'react';

export interface BarDatum {
  label: string;
  value: number;
  secondary?: string;
}

interface Props {
  data: BarDatum[];
  height?: number;
  formatValue: (v: number) => string;
  /** Show every nth x label (auto if omitted) */
  labelEvery?: number;
  ariaLabel: string;
  color?: string;
}

/**
 * Minimal single-series bar chart (SVG, no dependencies).
 * One hue, thin rounded bars anchored to the baseline, recessive grid,
 * hover tooltip. Text always uses ink colors, never the series color.
 */
const BarChart: React.FC<Props> = ({ data, height = 180, formatValue, labelEvery, ariaLabel, color = '#EB2D40' }) => {
  const [hover, setHover] = useState<number | null>(null);
  const width = 640;
  const padL = 52;
  const padR = 8;
  const padT = 20;
  const padB = 24;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const n = Math.max(1, data.length);
  const max = Math.max(1, ...data.map((d) => d.value));
  const slot = innerW / n;
  const barW = Math.max(3, Math.min(28, slot - 2));
  const every = labelEvery || Math.max(1, Math.ceil(n / 8));
  const ticks = [0, 0.5, 1];

  if (data.every((d) => d.value === 0)) {
    return (
      <div className="h-[180px] flex items-center justify-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-md">
        —
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label={ariaLabel}>
        {ticks.map((tk) => {
          const y = padT + innerH - tk * innerH;
          return (
            <g key={tk}>
              <line x1={padL} x2={width - padR} y1={y} y2={y} stroke="#E5E7EB" strokeWidth={1} />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={10} fill="#6B7280">
                {formatValue(max * tk)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = (d.value / max) * innerH;
          const x = padL + i * slot + (slot - barW) / 2;
          const y = padT + innerH - h;
          const active = hover === i;
          const base = padT + innerH;
          const r = Math.min(4, h / 2, barW / 2);
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onTouchStart={() => setHover(i)}>
              <rect x={padL + i * slot} y={padT} width={slot} height={innerH} fill="transparent" />
              {h > 0 && (
                <path
                  d={`M${x},${base} V${y + r} A${r},${r} 0 0 1 ${x + r},${y} H${x + barW - r} A${r},${r} 0 0 1 ${x + barW},${y + r} V${base} Z`}
                  fill={color}
                  opacity={hover === null || active ? 1 : 0.55}
                />
              )}
              {i % every === 0 && (
                <text x={padL + i * slot + slot / 2} y={height - 8} textAnchor="middle" fontSize={10} fill="#6B7280">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover !== null && data[hover] && (
        <div
          className="pointer-events-none absolute top-0 bg-gray-900 text-white text-xs rounded-md px-2 py-1 shadow"
          style={{ left: `${((hover + 0.5) / n) * 100}%`, transform: 'translateX(-50%)' }}
        >
          <div className="font-medium">{data[hover].label}</div>
          <div>{formatValue(data[hover].value)}</div>
          {data[hover].secondary && <div className="opacity-80">{data[hover].secondary}</div>}
        </div>
      )}
    </div>
  );
};

export default BarChart;
